/**
 * My Rituality Dashboard Page
 * Personal dashboard showing practice journey: profile, stats, reflection,
 * reminders, daily rituals, completed rituals, and tabbed content.
 * Forces dark theme on mount (like Connect page pattern), restores on unmount.
 */

import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { useTheme } from "@/context/ThemeContext"
import { useAuthStore } from "@/features/auth/store/authStore"
import { BookingConfirmation } from "@/features/booking/components/BookingConfirmation"
import { useFollowRealtime } from "@/features/profile/hooks/useFollowRealtime"
import { useProfileStatistics } from "@/features/profile/hooks/useProfileStatistics"
import { useIsMobile } from "@/hooks/useIsMobile"
import { getAssetUrl } from "@/utils/assets"
import { DailyRitualsSection } from "../components/DailyRitualsSection"
import { IntentionsSection } from "../components/IntentionsSection"
import { LatestRitualsSection } from "../components/LatestRitualsSection"
import { MyRitualityProfileHeader } from "../components/MyRitualityProfileHeader"
import { ReflectionSection } from "../components/ReflectionSection"
import { RemindersSection } from "../components/RemindersSection"
import { RitualFriendCircle } from "../components/RitualFriendCircle"
import {
  RitualsCommunitiesSection,
  type RitualsCommunitiesSectionHandle,
} from "../components/RitualsCommunitiesSection"
import { StatsSection } from "../components/StatsSection"
import { usePracticeStatistics } from "../hooks/usePracticeStatistics"

export function MyRitualityScreen() {
  const { theme, setTheme } = useTheme()
  const previousThemeRef = useRef(theme)
  const user = useAuthStore(state => state.user)
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showBookingConfirmation, setShowBookingConfirmation] = useState(false)
  const ritualsSectionRef = useRef<RitualsCommunitiesSectionHandle>(null)
  const [bookingDetails, setBookingDetails] = useState<{
    id: string
    ritualTitle: string
    date: string
    startTime: string
    endTime: string
    duration?: string
    location?: string
    sessionType?: string
    coachName: string | null
    websiteUrl?: string
    fee: number | null
    feeCurrency?: string | null
    expertId?: string
  } | null>(null)

  useFollowRealtime()

  // Handle payment status from Stripe redirect
  useEffect(() => {
    const payment = searchParams.get("payment")
    if (payment === "success") {
      // Check if there are confirmed booking details in localStorage
      const confirmedBooking = localStorage.getItem("confirmedBookingDetails")
      if (confirmedBooking) {
        try {
          const booking = JSON.parse(confirmedBooking)
          setBookingDetails(booking)
          setShowBookingConfirmation(true)
          // Clean up localStorage
          localStorage.removeItem("confirmedBookingDetails")
          localStorage.removeItem("pendingBookingConfirmation")
        } catch (e) {
          console.error("Failed to parse booking details:", e)
          toast.success("Payment successful! Your coaching session has been booked.")
        }
      } else {
        toast.success("Payment successful! Your coaching session has been booked.")
      }
      setSearchParams({}, { replace: true })
    } else if (payment === "cancelled") {
      toast.error("Payment was cancelled.")
      localStorage.removeItem("pendingBookingConfirmation")
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const handleCloseBookingConfirmation = () => {
    setShowBookingConfirmation(false)

    // Redirect to expert's profile with #session anchor
    if (bookingDetails?.expertId) {
      navigate(`/profile/${bookingDetails.expertId}#session`)
    } else {
      // Fallback to my-rituality if no expert ID
      navigate("/my-rituality?tab=sessions&subTab=booked")
    }

    setBookingDetails(null)
  }

  // Force dark theme on mount, restore on unmount
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only effect to set/restore theme
  useEffect(() => {
    previousThemeRef.current = theme
    if (theme !== "dark") {
      setTheme("dark")
    }
    return () => {
      if (previousThemeRef.current !== "dark") {
        setTheme(previousThemeRef.current)
      }
    }
  }, [])

  // Fetch statistics once at the page level and pass down as props
  const { data: practiceStatsResponse, isLoading: practiceStatsLoading } = usePracticeStatistics(
    user?.id || "",
  )
  const { data: profileStatsResponse } = useProfileStatistics(user?.id || "")
  const profileStatistics = profileStatsResponse?.data

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="h-8 w-32 bg-white/5 rounded animate-pulse" />
      </div>
    )
  }

  return (
    <div className="min-h-screen relative">
      {/* Booking Confirmation Modal */}
      {showBookingConfirmation && bookingDetails && (
        <BookingConfirmation
          open
          onClose={handleCloseBookingConfirmation}
          booking={{
            ...bookingDetails,
            isCoachingBooking: true,
          }}
        />
      )}

      <div className="md:pt-8">
        {/* Section 1: Profile Header */}
        <MyRitualityProfileHeader
          statistics={profileStatistics}
          onFollowersClick={() => ritualsSectionRef.current?.scrollToTab("followers", "followers")}
          onFollowingClick={() => ritualsSectionRef.current?.scrollToTab("followers", "following")}
        />

        {/* Section 1.5: Ritual Friend Circle */}
        <div className="mt-8 max-w-360 mx-auto px-5 md:px-17.5 ">
          <RitualFriendCircle />
        </div>

        {/* Section 2: Your Intentions */}
        <div className="mt-12 max-w-360 mx-auto px-5 md:px-17.5 ">
          <IntentionsSection />
        </div>

        {/* Section 3: Your Stats */}
        <div className="mt-12 max-w-360 mx-auto px-5 md:px-17.5 ">
          <StatsSection
            practiceStats={practiceStatsResponse?.data}
            profileStatistics={profileStatistics}
            isLoading={practiceStatsLoading}
          />
        </div>

        {/* Section 4: AI Reflection Prompt */}
        <div
          className={`mt-16 max-w-360 mx-auto px-5 md:px-17.5 py-7.5 md:py-0 ${isMobile ? "bg-cover bg-no-repeat bg-center bg-blur-[25px]" : ""}`}
          style={
            isMobile ? { backgroundImage: `url("${getAssetUrl("chat_banner.png")}")` } : undefined
          }
        >
          <ReflectionSection />
        </div>

        {/* Section 5: Reminders for Your Growth */}
        <div className="mt-10 md:mt-16 max-w-360 mx-auto px-5 md:px-17.5 ">
          <RemindersSection />
        </div>

        {/* Section 6: Your Daily Rituals */}
        <div className="mt-10 md:mt-16 max-w-360 mx-auto px-5 md:px-17.5 ">
          <DailyRitualsSection />
        </div>

        {/* Section 7: Your Latest Rituals */}
        <div className="mt-10 md:mt-16 max-w-360 mx-auto px-5 md:px-17.5">
          <LatestRitualsSection />
        </div>

        {/* Section 8: Your Rituals & Communities */}
        <div className="mt-10 md:mt-16">
          <RitualsCommunitiesSection
            ref={ritualsSectionRef}
            userId={user.id}
            statistics={profileStatistics}
          />
        </div>
      </div>
    </div>
  )
}

export default MyRitualityScreen
